/**
 * ColumnNumberRow — `column.number` renderer. Paints a localized number
 * in the `column` slot and edits its parent dataSource's cells.
 * Optional `unit`, `format`, and `prompt` attributes default to the
 * source's attribute bag so they round-trip through serialize → hydrate
 * even while the renderer is auto-hydrated (and thus skipped in serialize).
 * Copy-on-write: once the default renderer is materialized + customized, the
 * customized value lives on the renderer's OWN bag; the getters dual-read
 * (renderer first, source fallback).
 */

import type { Option } from "../../core/option.ts";
import { Json, type JsonObject } from "../../core/json.ts";
import type { NaiveDate } from "@tento-chrono";

import type { Actor } from "../../tx/invalidation.ts";
import type { Durable } from "../../core/durable.ts";
import type { ModelRowOptions } from "../row-options.ts";
import { RendererRow } from "./renderer-row.ts";
import { SourceCells } from "./source-cells.ts";
import { SourceRow } from "./source-row.ts";
import { sourceWriterOf } from "./source-row.ts";

/**
 * **The writers of a `column.number` renderer** — checked and durable number
 * writes and clears of its source row's cells (`ColumnNumberRow.Writer`),
 * each through the source's checked writer (`sourceWriterOf`). Not on the row: a
 * row a read hands out (`handle.model`, `row.variant`, `RowKind.of`) —
 * and the live class `liveRowOf` reaches — carries no writer. The package's
 * own ops write through it, and this repository's tests through
 * `@tento-lona/sheets/internal-testing`; an app and the SDK's own front-ends
 * (sheets-ui, the CLI) write the cell through the SDK row's checked verbs
 * (`row.writableSingle()?.write(CellTarget.date(day), value, actor)`).
 */
export function columnNumberWriterOf(row: ColumnNumberRow): ColumnNumberRow.Writer {
  const source = (): SourceRow.Writer => sourceWriterOf(row.source);
  return Object.freeze({
    row,
    write: async (date: NaiveDate.Partial, value: number, actor: Actor) => {
      await source().write<number>(date, value, null, actor);
    },
    clear: async (date: NaiveDate.Partial, actor: Actor) => {
      await source().clear(date, null, actor);
    },
    writeDurable: (date: NaiveDate.Partial, value: number, actor: Actor) =>
      source().writeDurable<number>(date, value, null, actor),
    clearDurable: (date: NaiveDate.Partial, actor: Actor) => source().clearDurable(date, null, actor),
  });
}

/**
 * The renderer row that shows its source row's numbers, one per column, with
 * a number format.
 */
export class ColumnNumberRow extends RendererRow {
  get source(): SourceRow {
    const p = this.parent;
    if (!(p instanceof SourceRow)) {
      throw new Error(
        `ColumnNumberRow: parent of ${this.id.rawId} is not a SourceRow ` +
          `(got ${p?.constructor.name ?? "undefined"}).`,
      );
    }
    return p;
  }

  /**
   * False when the source rejects cell writes (formula rows,
   * provider-owned `metadata.readOnly` rows) — the UI renders the
   * cell read-only instead of letting a number write throw.
   */
  get editable(): boolean {
    // Detached renderer (stale onData after in-place structural mutation)
    // reports non-editable instead of letting `source` throw. See ColumnTextRow.
    const p = this.parent;
    return p instanceof SourceRow ? p.cellsWritable : false;
  }

  /** The loaded number for `date` (a synchronous cache read); `null` if unset. */
  peekNumber(date: NaiveDate.Partial): Option<number> {
    const cell = SourceCells.dates(this.source.peekCells(date.toRange()))[0];
    return cell == null ? null : (cell.value as Option<number>);
  }





  /** Unit label (e.g. "USD", "bpm"). Dual-read: renderer bag, source fallback. */
  get unit(): Option<string> {
    return (this.attributesAs(ColumnNumberRow.decodeAttrs)?.unit ??
      this.source.attributesAs(ColumnNumberRow.decodeAttrs)?.unit) ?? null;
  }

  /**
   * Display format — `"number" | "currency" | "percentage"`. Stored
   * as `type` for historical reasons; exposed here as `format` to match
   * the `NumberInput.bind` API the plugin consumes. Copy-on-write dual-read:
   * the renderer's OWN bag wins once materialized + customized, else the
   * source's bag (where it lives until first customization).
   */
  get format(): Option<ColumnNumberRow.Format> {
    return (this.attributesAs(ColumnNumberRow.decodeAttrs)?.type ??
      this.source.attributesAs(ColumnNumberRow.decodeAttrs)?.type) ?? null;
  }

  /** Placeholder prompt shown on the current-column cell. Dual-read. */
  get prompt(): Option<string> {
    return (this.attributesAs(ColumnNumberRow.decodeAttrs)?.prompt ??
      this.source.attributesAs(ColumnNumberRow.decodeAttrs)?.prompt) ?? null;
  }

  /**
   * Display-format option. Copy-on-write: customizing it promotes the
   * synthetic default renderer to a real row (`ensureRendererMaterialized`),
   * so the option targets `renderer-attr` on this renderer (`type`), which the
   * materialized row persists through serialize → hydrate. The option write (`RowEnvelopeDrive.writeTarget`)
   * materializes before the write lands; `format` above dual-reads so an
   * un-materialized renderer still paints the source's value.
   */
  override optionContributions(): readonly ModelRowOptions.Contribution[] {
    const source = this.parent;
    if (!(source instanceof SourceRow)) return [];
    return [
      {
        key: "number:format",
        label: "Format",
        tier: "section",
        priority: 50,
        section: "Format",
        target: { kind: "renderer-attr", rowId: this.id, attr: "type" },
        control: {
          kind: "select",
          options: [
            { value: "number", label: "Number" },
            { value: "currency", label: "Currency" },
            { value: "percentage", label: "Percent" },
          ],
        },
      },
    ];
  }

  override toDebug(): Record<string, unknown> {
    const u = this.unit;
    const f = this.format;
    const p = this.prompt;
    return {
      ...super.toDebug(),
      ...(u !== undefined ? { unit: u } : {}),
      ...(f !== undefined ? { format: f } : {}),
      ...(p !== undefined ? { prompt: p } : {}),
    };
  }
}

/** Types and values that go with `ColumnNumberRow`: `Writer`, `Format`, `Attrs`, `decodeAttrs`. */
export namespace ColumnNumberRow {
  /**
   * A `column.number` renderer's writers, reached through
   * `columnNumberWriterOf(row)` only (see there).
   */
  export interface Writer {
    /** The row these writers write. */
    readonly row: ColumnNumberRow;
    /** Checked number write: resolves when the backend accepted it, rejects with its error otherwise. */
    write(date: NaiveDate.Partial, value: number, actor: Actor): Promise<void>;
    /** Checked number clear: the cleared cell persists (and recomputes dependents). */
    clear(date: NaiveDate.Partial, actor: Actor): Promise<void>;
    /** Durable number write — the per-keystroke form: shown at once, persisted on the trailing debounce. */
    writeDurable(date: NaiveDate.Partial, value: number, actor: Actor): Durable<void, SourceRow.Persisted>;
    /** Durable number clear — the per-keystroke form of `clear` (an emptied editor). */
    clearDurable(date: NaiveDate.Partial, actor: Actor): Durable<void, SourceRow.Persisted>;
  }

  export type Format = "number" | "currency" | "percentage";

  export type Attrs = Partial<{
    unit: string;
    type: Format;
    prompt: string;
  }>;

  const FORMATS: readonly Format[] = ["number", "currency", "percentage"];

  /** Read a number row's display keys (`unit`, `type`, `prompt`) off its bag. */
  export function decodeAttrs(attributes: JsonObject): Attrs {
    const unit = Json.string(attributes, "unit");
    const type = Json.oneOf(attributes, "type", FORMATS);
    const prompt = Json.string(attributes, "prompt");
    return {
      ...(unit != null ? { unit } : {}),
      ...(type != null ? { type } : {}),
      ...(prompt != null ? { prompt } : {}),
    };
  }
}
